import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import CollectionList from '@/se/components/collection/CollectionList';
import { TruncatedText } from '@/se/components/typography';
import { sortNumber, sortString } from '@/util/sort';
import { openBlockAnalytics } from '@/graph/bookings';
import { MetricTooltip } from '@/components/pages/blockSchedule/metrics/MetricTooltip';
import { OpenBlockMetrics } from './Metrics';
import { CapacityBar, PercentageRingCell } from './PhysicianPerformance';
import { actualColor, utilizedColor } from './Cell';
import { exportPhysicianCSV, exportPhysicianPDF, exportPhysicianXLSX } from './physicianExport';

const columns = [
  {
    title: 'Physician',
    lens: data => data?.physician?.name ?? 'Unknown physician',
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
    sortFunction: (l, r) => sortString(l?.physician?.name, r?.physician?.name),
  },
  {
    title: 'Speciality',
    lens: data => data?.physician?.speciality,
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
    sortFunction: (l, r) => sortString(l?.physician?.speciality, r?.physician?.speciality),
  },
  {
    title: 'Scheduled',
    key: 'percentage',
    lens: data => ({
      percentage: data?.scheduledPercentage ?? 0,
      hours: data?.scheduledHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color={utilizedColor} />
    ),
    sortFunction: (l, r) => sortNumber(l?.scheduledHours, r?.scheduledHours),
    InfoComponent: () => (
      <MetricTooltip
        title="Scheduled %"
        description="The share of all open block time that has this physician’s cases scheduled. (= Time for Physician’s Cases Scheduled in Open Blocks ÷ Open Block Hours × 100)"
      />
    ),
  },
  {
    title: 'Actual',
    key: 'percentage',
    lens: data => ({
      percentage: data?.actualPercentage ?? 0,
      hours: data?.actualHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color={actualColor} />
    ),
    sortFunction: (l, r) => sortNumber(l?.actualHours, r?.actualHours),
    InfoComponent: () => (
      <MetricTooltip
        title="Actual %"
        description="The share of all open block time this physician actually used for surgeries (measured wheels in → wheels out). (= Physician’s Wheels Time in Open Blocks ÷ Open Block Hours × 100)"
      />
    ),
  },
  {
    title: '',
    lens: data => ({
      scheduledPct: data?.scheduledPercentage ?? 0,
      usedPct: data?.actualPercentage ?? 0,
    }),
    Component: ({ data }) => <CapacityBar usedPct={data?.usedPct || 0} scheduledPct={data?.scheduledPct || 0} />,
    sortDisabled: true,
  },
];

const OpenBlockAnalytics = ({ date, filter }) => {
  const dateRange = filter?.dateRange;
  const query = useQuery(openBlockAnalytics, {
    variables: { filter: { dateRange } },
    skip: !dateRange,
    fetchPolicy: 'cache-and-network',
  });
  const summary = query.data?.openBlockAnalytics;

  const exportFileName = useMemo(() => `${format(date ?? new Date(), 'MMMM YYYY')} Open Block Analytics Data`, [date]);

  return (
    <>
      <OpenBlockMetrics summary={summary} loading={query.loading && !summary} date={date} />
      <CollectionList
        idProvider={item => item.physicianId}
        entityName={'Physicians'}
        tableKey={'open-block-physicians'}
        defaultSort={{ title: 'Scheduled', type: 'DESC' }}
        columns={columns}
        data={query}
        getList={payload => payload?.data?.openBlockAnalytics?.physicians}
        useColumnSelection
        useCSVExport
        customExportCSV={exportPhysicianCSV}
        customExportXLSX={exportPhysicianXLSX}
        customExportPDF={exportPhysicianPDF}
        exportFileName={exportFileName}
        TitleAndActions={() => null}
        history={{}}
        location={{}}
      />
    </>
  );
};

export default OpenBlockAnalytics;
